// components/StickyNote.js - Unified component for both panels
import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-modal';
import './StickyNote.css';
import { useWindowOperations } from '../hooks/useWindowOperations';
import { sendNotesToAI } from '../api/aiNotesToContent';






const MIN_SIZE = { width: 50, height: 50 };

const StickyNote = ({ 
  sticky, 
  updateSticky, 
  removeSticky, 
  maxZIndex, 
  onFocus,
  onToggleDisplayMode,
  isSelected, // Changed from isPinned
  onSelectChange, // Changed from onPinChange
  isInMainPanel = false,  // Default to false (right panel) for backward compatibility
  registerWithZManager,   // Optional for z-index registration
  ...props
}) => {
  const {
    isExpanded,
    isMinimized,
    startDrag,
    startResize,
    toggleMinimize,
    toggleExpand
  } = useWindowOperations({ sticky, updateSticky, onFocus, isInMainPanel });
  const [localTitle, setLocalTitle] = useState(sticky.title || '');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [showAIMenu, setShowAIMenu] = useState(false);
  const [customPromptMode, setCustomPromptMode] = useState(false);
  const [customPromptText, setCustomPromptText] = useState("");
  const aiMenuRef = useRef(null);
  const [isProcessingAI, setIsProcessingAI] = useState(false);
  const [pendingAIOutput, setPendingAIOutput] = useState(null);
  const [aiError, setAiError] = useState(null);
   const [textSize, setTextSize] = useState('small'); // 'small', 'medium', 'large'
   const [isEditing, setIsEditing] = useState(false);
   const [perspectiveEnabled, setPerspectiveEnabled] = useState(false); // RIGHT - starts disabled  

   // NEW: Add state for drop menu on sticky
  const [showStickyDropMenu, setShowStickyDropMenu] = useState(false);
  const [stickyDropPosition, setStickyDropPosition] = useState({ x: 0, y: 0 });
  const [droppedTextContent, setDroppedTextContent] = useState('');

  const formatText = (text) => {
  if (!text) return '';
  
  return text
    // Headers
    .replace(/\n/g, '<br/>')                             // line breaks
    .replace(/^### (.*$)/gm, '<h3>$1</h3>')              // ### Header 3
    .replace(/^## (.*$)/gm, '<h2>$1</h2>')               // ## Header 2
    .replace(/^# (.*$)/gm, '<h1>$1</h1>')                // # Header 1
    
    // Text formatting
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')     // **bold**
    .replace(/\*(.*?)\*/g, '<em>$1</em>')                 // *italic*
    .replace(/`(.*?)`/g, '<code>$1</code>')               // `code`
    .replace(/__(.*?)__/g, '<u>$1</u>')                   // __underline__
    .replace(/~~(.*?)~~/g, '<del>$1</del>')               // ~~strikethrough~~
    .replace(/\^\^(.*?)\^\^/g, '<mark>$1</mark>')         // ^^highlight^^
    
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank">$1</a>') // [text](url)
    .replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank">$1</a>')     // Auto-link URLs
    
    // Lists (improved)
    .replace(/^- (.+)$/gm, '<li>$1</li>')                 // - bullet items
    .replace(/^\* (.+)$/gm, '<li>$1</li>')                // * bullet items  
    .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')             // 1. numbered items
    //.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')            // wrap in <ul>
    
    // Checkboxes
    .replace(/^\[ \] (.+)$/gm, '<label><input type="checkbox" disabled> $1</label>') // [ ] unchecked
    .replace(/^\[x\] (.+)$/gm, '<label><input type="checkbox" checked disabled> $1</label>') // [x] checked
    
    // Code blocks
    .replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>') // ```code blocks```
    
    // Quotes
    .replace(/^> (.+)$/gm, '<blockquote>$1</blockquote>')  // > quote
    
    // Horizontal rules
    .replace(/^---$/gm, '<hr>')                           // --- horizontal line
    
    // Line breaks (do this last)
    
};

  const AI_TASKS = {
    summarize: "Summarize the following content.",
    rephrase: "Rephrase the content in clearer language.",
    outline: "Convert this content into a structured outline.",
    custom: "__custom__"
  };

  const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}`;
};

  // Handle pin toggle (prevent sticky auto-minimize)
// Replace handlePinToggle with:
const handleSelectToggle = (e) => {
  e.stopPropagation();
  if (onSelectChange) {
    onSelectChange(sticky.id, !isSelected);
  }
};

const togglePerspective = () => {
  setPerspectiveEnabled(prev => !prev);
};

  // Handle display mode toggle (send to list view)
  const handleDisplayModeToggle = (e) => {
    e.stopPropagation();
    if (!sticky.note_id) return;
    onToggleDisplayMode(sticky.id, 'list');
  };

  // Handle sticky deletion
  const handleDelete = async () => {
    try {
      if (!sticky.note_id) return;
      setIsDeleteModalOpen(false);
      await removeSticky(sticky.id);
    } catch (error) {
      console.error('Error deleting sticky:', error);
    }
  };

  useEffect(() => {
    if (showAIMenu) {
      const handleClickOutside = (e) => {
        if (aiMenuRef.current && !aiMenuRef.current.contains(e.target)) {
          setShowAIMenu(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showAIMenu]);

  useEffect(() => {
  setLocalTitle(sticky.title || '');
}, [sticky.title]);




  const handleAISelection = async (task) => {
    if (task === '__custom__') {
      setCustomPromptMode(true); // Show input overlay
      setShowAIMenu(false);
      return;
    }
  
    const inputSticky = {
      id: sticky.id,
      type: sticky.type,
      content: sticky.content,
    };
  
    try {
      // Clear any previous errors or outputs
      setAiError(null);
      setPendingAIOutput(null);
      setIsProcessingAI(true); // 🟢 START spinner
      
      const response = await sendNotesToAI([inputSticky], task);
  
      if (!response) {
        setAiError("AI did not return a result.");
        return;
      }
  
      setPendingAIOutput(response); // Don't commit yet — wait for user decision
    } catch (error) {
      console.error("AI Error:", error);
      console.error("Error response status:", error.response?.status);
      console.error("Error response data:", error.response?.data);
      console.error("Error message:", error.message);
      
      // Generate user-friendly error message based on specific error types
      let errorMessage = "Failed to process content with AI.";
      
      // Check for token-related errors in either the message or response
      if (error.message === 'Insufficient token balance' || 
          (error.response?.data?.detail && error.response.data.detail.includes('out of tokens'))) {
        errorMessage = "⚠️ Out of tokens. Please purchase more tokens to continue.";
      } else if (error.message === 'Authentication failed') {
        errorMessage = "⚠️ Authentication error. Please log in again.";
      } else if (error.message === 'AI composition failed') {
        // Check if this might actually be a token error being masked as a general error
        if (error.response?.status === 500) {
          // Server error - may be due to token issues
          console.log("Server error - checking for potential token issues");
          errorMessage = "⚠️ Service error. This may be due to insufficient tokens.";
        } else {
          errorMessage = "⚠️ AI service error. Please try again later.";
        }
      } else if (error.message && error.message.includes('rate limit')) {
        errorMessage = "⚠️ Rate limit exceeded. Please wait a moment and try again.";
      } else if (error.response?.status === 402) {
        // Direct check for payment required status code as backup
        errorMessage = "⚠️ Out of tokens. Please purchase more tokens to continue.";
      } else if (error.response?.status === 401) {
        // Authentication failed status code
        errorMessage = "⚠️ Authentication error. Please log in again.";
      } else if (error.response?.status === 400) {
        // Bad request (e.g., no stickies provided)
        errorMessage = "⚠️ Invalid request. Please ensure note contains content.";
      } else if (error.response?.status === 429) {
        // Rate limiting error
        errorMessage = "⚠️ Rate limit exceeded. Please wait a moment and try again.";
      } else if (error.response?.status === 500) {
        // Server error (e.g., OpenAI API error)
        errorMessage = "⚠️ Service error. This may be due to insufficient tokens.";
      }
      
      setAiError(errorMessage);
    } finally {
      setIsProcessingAI(false);
    }
  };
  
  // NEW: Handle drag over on sticky
  const handleStickyDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent container from handling
    e.dataTransfer.dropEffect = 'copy';
  };

  // NEW: Handle drop on sticky
  const handleStickyDrop = (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent container from handling
    
    const droppedText = e.dataTransfer.getData('text/plain') || 
                       e.dataTransfer.getData('text/html') || '';
    
    if (!droppedText.trim()) return;

    // Calculate position relative to sticky
    const stickyRect = e.currentTarget.getBoundingClientRect();
    const relativeX = e.clientX - stickyRect.left;
    const relativeY = e.clientY - stickyRect.top;
    
    setStickyDropPosition({ x: relativeX, y: relativeY });
    setDroppedTextContent(droppedText);
    setShowStickyDropMenu(true);
  };

  // NEW: Handle sticky drop menu actions
  const handleStickyDropAction = (action) => {
    setShowStickyDropMenu(false);
    
    switch(action) {
      case 'paste':
        // Append to existing content
        updateSticky(sticky.id, {
          content: sticky.content + '\n\n' + droppedTextContent
        });
        break;
      case 'replace':
        // Replace existing content
        updateSticky(sticky.id, {
          content: droppedTextContent
        });
        break;
      case 'prepend':
        // Add to beginning
        updateSticky(sticky.id, {
          content: droppedTextContent + '\n\n' + sticky.content
        });
        break;
    }
    setDroppedTextContent('');
  };

  // Add click outside handler for sticky drop menu
  useEffect(() => {
    if (showStickyDropMenu) {
      const handleClickOutside = (e) => {
        if (!e.target.closest('.sticky-drop-menu')) {
          setShowStickyDropMenu(false);
        }
      };
      
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showStickyDropMenu]);
  
// Add this function before the return statement
const getTextSizeValue = (size) => {
  switch(size) {
    case 'small': return '18px';
    case 'medium': return '20px';
    case 'large': return '25px';
    default: return '18px';
  }
};

const toggleTextSize = () => {
  const sizes = ['small', 'medium', 'large'];
  const currentIndex = sizes.indexOf(textSize);
  const nextIndex = (currentIndex + 1) % sizes.length;
  setTextSize(sizes[nextIndex]);
};

const getTextSizeIcon = (size) => {
  switch(size) {
    case 'small': return 'A⁻';
    case 'medium': return 'A';
    case 'large': return 'A⁺';
    default: return 'A';
  }
};


  return (
    <div
      className={`sticky ${isMinimized ? 'minimized' : ''} ${sticky.type === 'ai' ? 'ai-sticky' : ''} ${!isInMainPanel ? 'right-panel-perspective' : ''} ${perspectiveEnabled ? 'perspective-enabled' : ''}`}
      style={{
        top: isInMainPanel ? sticky.main_y : sticky.y,
        left: isInMainPanel ? sticky.main_x : sticky.x,
        width: isInMainPanel ? sticky.main_width : sticky.width,
        height: isInMainPanel ? sticky.main_height : sticky.height,
        zIndex: sticky.zIndex
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (sticky.note_id) {
          onFocus?.();
        }
      }}
       onDragOver={handleStickyDragOver}
      onDrop={handleStickyDrop}
    >

      <div className="drag-bar" onMouseDown={startDrag} onDoubleClick={toggleMinimize}>
        <div className="sticky-pin">
        <input 
          type="checkbox"
          checked={isSelected} // Changed from isPinned
          onChange={handleSelectToggle} // Changed from handlePinToggle
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          title="Select for AI/Pin/Delete"
        />
      </div>
        <input 
            type="text"
            className="sticky-title-input"
            value={localTitle}
            onChange={(e) => {
              //console.log('🎯 TITLE onChange - calling updateSticky with:', { title: e.target.value }); // ADD THIS
              setLocalTitle(e.target.value);
              updateSticky(sticky.id, { title: e.target.value });
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            placeholder="Untitled..."
          />
            <div className="sticky-id" style={{ 
                  fontSize: '10px', 
                  color: '#666', 
                  marginRight: '5px' 
                }}>
                  #{sticky.id}
                </div>
          <div className="sticky-date">
            {formatDate(sticky.created_at)}
          </div>

        <div className="sticky-controls">
          {!isInMainPanel && (
            <button onClick={(e) => {
              e.stopPropagation();
              togglePerspective();
            }} title={perspectiveEnabled ? "Disable 3D effect" : "Enable 3D effect"}>
              {perspectiveEnabled ? '📐' : '📏'}
            </button>
          )}
            <button onClick={(e) => {
              e.stopPropagation();
              toggleTextSize();
            }} title={`Text size: ${textSize}`}>
              {getTextSizeIcon(textSize)}
            </button>

            <button onClick={(e) => {
              e.stopPropagation();
              toggleExpand();
            }}>
              {isExpanded ? '⊙' : '⊕'}
            </button>

            <button onClick={(e) => {
              e.stopPropagation();
              onToggleDisplayMode(sticky.id, 'list');
            }} title="Send to List">
              ▼
            </button>

            <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAIMenu(prev => !prev);
                  }} 
                  title="Run AI task on this sticky"
                >
                  🤖
                </button>

            <button onClick={() => setIsDeleteModalOpen(true)}>✖</button>
          </div>

      </div>


      {!isMinimized && (
  <>
    {isEditing ? (
      // Edit mode - plain textarea
      <textarea
        value={sticky.content}
        onChange={(e) => updateSticky(sticky.id, { content: e.target.value })}
        onClick={(e) => e.stopPropagation()}
        onBlur={() => setIsEditing(false)}
        onFocus={() => {
          onFocus?.();
          if (isInMainPanel) {
            updateSticky(sticky.id, {
              main_x: sticky.main_x,
              main_y: sticky.main_y,
            });
          }
        }}
        autoFocus
        style={{ fontSize: getTextSizeValue(textSize) }}
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = 'copy';
          e.dataTransfer.dropEffect = 'copy';
        }}
      />
    ) : (
      // Display mode - formatted content
      <div
        className="sticky-formatted-content"
        style={{ fontSize: getTextSizeValue(textSize) }}
        onClick={(e) => {
          e.stopPropagation();
          setIsEditing(true);
        }}
        dangerouslySetInnerHTML={{ 
          __html: formatText(sticky.content) 
        }}
      />
    )}
    <div className="resize-handle" onMouseDown={startResize} />
  </>
)}
           {showStickyDropMenu && (
        <div 
          className="sticky-drop-menu" 
          style={{ 
            position: 'absolute', 
            left: stickyDropPosition.x, 
            top: stickyDropPosition.y,
            zIndex: 10001,
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
            padding: '8px',
            minWidth: '160px'
          }}
        >
          <div style={{ padding: '4px 12px', fontWeight: 'bold', fontSize: '12px' }}>
            Paste text into this sticky:
          </div>
          
          <button 
            onClick={() => handleStickyDropAction('paste')}
            style={{ 
              display: 'block', width: '100%', textAlign: 'left',
              padding: '6px 12px', margin: '2px 0', border: 'none',
              borderRadius: '3px', cursor: 'pointer', backgroundColor: 'transparent'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#f5f5f5'}
            onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            📎 Append to end
          </button>
          
          <button 
            onClick={() => handleStickyDropAction('prepend')}
            style={{ 
              display: 'block', width: '100%', textAlign: 'left',
              padding: '6px 12px', margin: '2px 0', border: 'none',
              borderRadius: '3px', cursor: 'pointer', backgroundColor: 'transparent'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#f5f5f5'}
            onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            📌 Add to beginning
          </button>
          
          <button 
            onClick={() => handleStickyDropAction('replace')}
            style={{ 
              display: 'block', width: '100%', textAlign: 'left',
              padding: '6px 12px', margin: '2px 0', border: 'none',
              borderRadius: '3px', cursor: 'pointer', backgroundColor: 'transparent'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#f5f5f5'}
            onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            🔄 Replace content
          </button>
        </div>
      )}

      <Modal
        isOpen={isDeleteModalOpen}
        onRequestClose={() => setIsDeleteModalOpen(false)}
        className="modal-content"
        overlayClassName="modal-overlay"
        style={{
          overlay: {
            zIndex: 99999, // Very high z-index to ensure it's on top of everything
          },
          content: {
            zIndex: 100000 // Even higher z-index for the content
          }
        }}
      >
        <h3>Delete Sticky Note?</h3>
        <p>Are you sure you want to delete this sticky note?</p>
        <div className="modal-actions">
          <button onClick={handleDelete}>Delete</button>
          <button onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
        </div>
      </Modal>

     {customPromptMode && (
  <div 
    className="ai-custom-prompt-overlay"
    style={{
      position: 'absolute',
      top: '45px',
      right: (isInMainPanel ? sticky.main_width : sticky.width) < 340 ? undefined : '5px',
      left: (isInMainPanel ? sticky.main_width : sticky.width) < 340 ? '5px' : undefined,
      zIndex: 10000,
      backgroundColor: '#fff',  
      border: '1px solid #ccc',
      borderRadius: '6px',
      padding: '10px',
      width: '320px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.25)'
    }}
  >
    <textarea
      value={customPromptText}
      onChange={(e) => setCustomPromptText(e.target.value)}
      rows={3}
      placeholder="Enter your custom AI task..."
      style={{
        width: '100%',
        fontSize: '13px',
        borderRadius: '4px',
        padding: '6px',
        border: '1px solid #bbb',
        resize: 'none'
      }}
    />
    <div style={{ marginTop: '8px', textAlign: 'right' }}>
      <button
        style={{ marginRight: '6px' }}
        onClick={() => {
          setCustomPromptText('');
          setCustomPromptMode(false);
        }}
      >
        Cancel
      </button>
      
      <button
  onClick={async () => {
    if (!customPromptText.trim()) return;
  
    // 🚀 Close the overlay *immediately*
    setCustomPromptMode(false);
  
    // 🔄 Start spinner
    setIsProcessingAI(true);
  
    try {
      // Clear any previous errors or outputs
      setAiError(null);
      setPendingAIOutput(null);
      
      const response = await sendNotesToAI(
        [{ id: sticky.id, content: sticky.content, type: sticky.type }],
        customPromptText.trim()
      );
  
      if (!response) {
        setAiError("AI did not return a result for your custom prompt.");
        return;
      }
  
      setPendingAIOutput(response); // Trigger choice menu
    } catch (err) {
      setAiError(err.message || "Failed to generate AI response");
      console.error(err);
    } finally {
      setCustomPromptText('');
      setIsProcessingAI(false);
    }
  }}
      >
        Run
      </button>
    </div>
  </div>
)}


      {showAIMenu && (
  <div 
    ref={aiMenuRef}
    className="ai-menu-popover"
    style={{
      position: 'absolute',
      top: '42px',
      right: '5px',
      backgroundColor: '#fff',
      border: '1px solid #ccc',
      borderRadius: '6px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
      padding: '6px 0',
      zIndex: 9999,
      width: '180px'
    }}
  >
    {[
        { label: '✂️ Summarize', task: AI_TASKS.summarize },
        { label: '🧠 Rephrase', task: AI_TASKS.rephrase },
        { label: '🗂 Turn into outline', task: AI_TASKS.outline },
        { label: '➕ Custom Prompt...', task: AI_TASKS.custom }
    ].map((option, idx) => (
      <div 
        key={idx}
        onClick={(e) => {
          e.stopPropagation();
          setShowAIMenu(false);
          handleAISelection(option.task);
        }}
        style={{
          padding: '8px 12px',
          cursor: 'pointer',
          fontSize: '14px',
          borderBottom: idx < 4 ? '1px solid #eee' : 'none'
        }}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
      >
        {option.label}
      </div>
    ))}
  </div>
)}


{isProcessingAI && (
  <div 
    className="processing-indicator"
    style={{
      position: 'absolute',
      top: '45px', 
      right: '10px',
      width: '32px',
      height: '32px',
      zIndex: 10000,
      pointerEvents: 'none'
    }}
  >
    <svg viewBox="0 0 100 100" width="100%" height="100%">
      <path
        className="snowflake-path"
        d="M50 15L50 85M15 50L85 50M26 26L74 74M26 74L74 26"
        stroke="#4f46e5"
        strokeWidth="6"
        strokeLinecap="round"
      />
    </svg>
  </div>
)}
{pendingAIOutput && (
 <div className="ai-output-decision">
    <div style={{ marginBottom: '6px' }}>
      AI generated a result. What do you want to do with it?
    </div>
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
      <button onClick={() => {
        updateSticky(sticky.id, {
          content: `${sticky.content}\n\n--- AI Output ---\n${pendingAIOutput}`
        });
        setPendingAIOutput(null);
      }}>
        Append
      </button>
      <button
  onClick={async () => {
    try {
      // STEP 1: Ask parent to create a new sticky with high z-index
      const topZIndex = (props.maxZIndex || 100) + 10;
      const newStickyId = await props.addSticky?.(topZIndex, 'regular');

      // STEP 2: Proceed only if sticky was created
      if (newStickyId) {
        // STEP 3: Update sticky content and metadata
        await updateSticky(newStickyId, {
          content: pendingAIOutput,
          zIndex: topZIndex,
          note_id: sticky.note_id, // Maintain same note
          type: 'regular'
        });

        // STEP 4: Bring it to front
        props.onFocus?.(newStickyId); // Optional: only if this handler takes an ID
      }
    } catch (err) {
      console.error("❌ Failed to create new sticky from AI output:", err);
    } finally {
      setPendingAIOutput(null); // Clear the AI output buffer
    }
  }}
>
  New Sticky
</button>
    </div>
  </div>
)}
{aiError && (
  <div className="ai-output-decision" style={{backgroundColor: '#fef2f2', borderColor: '#f56565'}}>
    <div style={{ marginBottom: '6px', color: '#e53e3e', fontWeight: '500' }}>
      {aiError}
    </div>
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
      <button 
        onClick={() => setAiError(null)}
        style={{ backgroundColor: '#e53e3e', color: 'white' }}
      >
        Dismiss
      </button>
    </div>
  </div>
)}
    </div>
  );
};




// Comprehensive memo comparison function that works for both panels
const areStickyPropsEqual = (prevProps, nextProps) => {
  // First check common properties
  const basicPropsEqual = 
    prevProps.sticky.id === nextProps.sticky.id &&
    prevProps.sticky.content === nextProps.sticky.content &&
    prevProps.sticky.zIndex === nextProps.sticky.zIndex &&
    prevProps.sticky.is_minimized === nextProps.sticky.is_minimized &&
    prevProps.isPinned === nextProps.isPinned;

  // Check panel-specific properties
  const mainPanelPropsEqual = 
    prevProps.isInMainPanel === nextProps.isInMainPanel &&
    (!prevProps.isInMainPanel || (
      prevProps.sticky.main_x === nextProps.sticky.main_x &&
      prevProps.sticky.main_y === nextProps.sticky.main_y &&
      prevProps.sticky.main_width === nextProps.sticky.main_width &&
      prevProps.sticky.main_height === nextProps.sticky.main_height
    ));

  // Check right panel properties
  const rightPanelPropsEqual = 
    prevProps.isInMainPanel === nextProps.isInMainPanel &&
    (prevProps.isInMainPanel || (
      prevProps.sticky.x === nextProps.sticky.x &&
      prevProps.sticky.y === nextProps.sticky.y &&
      prevProps.sticky.width === nextProps.sticky.width &&
      prevProps.sticky.height === nextProps.sticky.height
    ));

  // Check handler functions
  const handlersEqual = 
    prevProps.updateSticky === nextProps.updateSticky &&
    prevProps.removeSticky === nextProps.removeSticky &&
    prevProps.onFocus === nextProps.onFocus &&
    prevProps.onToggleDisplayMode === nextProps.onToggleDisplayMode &&
    prevProps.onPinChange === nextProps.onPinChange &&
    prevProps.maxZIndex === nextProps.maxZIndex;

  return basicPropsEqual && mainPanelPropsEqual && rightPanelPropsEqual && handlersEqual;
};

export default React.memo(StickyNote, areStickyPropsEqual);